/**
 * Page « Analytics » — le site vu par Google Analytics 4.
 *
 * Reprend le rapport Looker Studio : KPIs, performances jour par jour, par
 * canal et par pays. Chiffres lus en direct chez GA4 (cf.
 * `traffic/ga4.controller.ts`) : utilisateurs et sessions sont ceux de GA4 à
 * l'unité près, ce qu'une somme de jours ne donnerait pas.
 *
 * CR% = achats GA4 ÷ sessions. GA4 ne voit pas tous les achats (bloqueurs,
 * consentement) : le CR% sur les commandes Shopify, lui, est sur le Résumé.
 */

import type { Ga4Metrics, Ga4Row } from '@shared/types/ga4';
import { useCallback } from 'react';

import { DonutChart, type DonutSlice } from '../components/DonutChart';
import { Ga4ConversionBars } from '../components/Ga4ConversionBars';
import { formatDuration, Ga4MetricsTable } from '../components/Ga4MetricsTable';
import { Ga4SeriesChart } from '../components/Ga4SeriesChart';
import { SalesTrendChart } from '../components/SalesTrendChart';
import { SectionShell } from '../components/SectionShell';
import { StatTile, type StatDelta } from '../components/StatTile';
import { useAuth } from '../hooks/useAuth';
import { useGa4Overview } from '../hooks/useTraffic';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { rateDelta, volumeDelta } from '../lib/deltas';
import { formatCompact, formatDayShort, formatDecimal, formatInteger, formatPercent } from '../lib/format';
import { CATEGORICAL_COLORS, OTHER_COLOR } from '../lib/theme';

const TOP_SLICES = 9;
/** Sous ce nombre de sessions, un CR% n'est que du bruit. */
const MIN_SESSIONS_CHANNEL = 50;
const MIN_SESSIONS_COUNTRY = 100;

const regionNames = new Intl.DisplayNames(['fr'], { type: 'region' });
const countryName = (code: string): string => {
  if (!/^[A-Z]{2}$/.test(code)) return code === '(not set)' ? 'Inconnu' : code;
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
};

/** Neuf premières parts, puis « Autres ». */
function shareSlices(rows: readonly Ga4Row[], pick: (row: Ga4Row) => number, label: (value: string) => string): DonutSlice[] {
  const sorted = [...rows].filter((row) => pick(row) > 0).sort((a, b) => pick(b) - pick(a));
  const slices: DonutSlice[] = sorted.slice(0, TOP_SLICES).map((row, index) => ({
    key: row.value,
    label: label(row.value),
    value: pick(row),
    color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
  }));
  const others = sorted.slice(TOP_SLICES).reduce((sum, row) => sum + pick(row), 0);
  if (others > 0) slices.push({ key: 'others', label: 'Autres', value: others, color: OTHER_COLOR });
  return slices;
}

export function Ga4Page() {
  const { token, signOut } = useAuth();
  const [period] = usePeriodParam();
  const query = useGa4Overview({ from: period.from, to: period.to, token, onUnauthorized: signOut });

  const stats = query.data;
  const totals = stats?.comparison.current.totals;
  const overlap = stats?.comparison.comparisonsOverlap ?? false;
  const refs = overlap
    ? [{ label: "l'an dernier", key: 'yearAgo' as const }]
    : [
        { label: 'période préc.', key: 'previous' as const },
        { label: "l'an dernier", key: 'yearAgo' as const },
      ];
  const available = (key: 'previous' | 'yearAgo') => (stats?.comparison[key].totals.sessions ?? 0) > 0;
  const volume = (pick: (t: Ga4Metrics) => number | null, format: (value: number) => string, higherIsBetter: boolean | null = true): StatDelta[] =>
    refs.map(({ label, key }) =>
      volumeDelta({ label, available: available(key) }, totals ? pick(totals) : null, stats ? pick(stats.comparison[key].totals) : null, format, higherIsBetter, 'pas de données GA4'),
    );
  const rate = (pick: (t: Ga4Metrics) => number | null): StatDelta[] =>
    refs.map(({ label, key }) =>
      rateDelta({ label, available: available(key) }, totals ? pick(totals) : null, stats ? pick(stats.comparison[key].totals) : null, true, 'pas de données GA4'),
    );

  const dates = (stats?.daily ?? []).map((day) => day.date);
  const dailyDesc = [...(stats?.daily ?? [])].reverse();
  const channelLabel = useCallback((value: string) => value, []);
  const conversionRows = (rows: readonly Ga4Row[], label: (value: string) => string) =>
    rows.map((row) => ({ label: label(row.value), sessions: row.sessions, purchases: row.purchases, conversionPercent: row.conversionPercent }));

  return (
    <SectionShell
      lastSyncAt={stats?.meta.generatedAt}
      isPending={query.isPending}
      isFetching={query.isFetching}
      error={query.error}
      hasData={Boolean(stats)}
      title={
        <h1 className="page-title">
          Analytics
          <span className="page-title__sub">Le site vu par Google Analytics 4 — chiffres lus en direct, identiques à GA4</span>
        </h1>
      }
    >
      <h2 className="section-heading">GA4 KPIs</h2>
      <div className="kpi-row">
        <StatTile label="Nombre total d'utilisateurs" value={formatInteger(totals?.users ?? 0)} deltas={volume((t) => t.users, formatInteger)} hint="visiteurs distincts de la période" />
        <StatTile label="Sessions" value={formatInteger(totals?.sessions ?? 0)} deltas={volume((t) => t.sessions, formatInteger)} />
        <StatTile
          label="Vues par session"
          value={totals?.viewsPerSession != null ? formatDecimal(totals.viewsPerSession) : '—'}
          deltas={volume((t) => t.viewsPerSession, formatDecimal)}
        />
        <StatTile label="Ajouts au panier" value={formatInteger(totals?.addToCarts ?? 0)} deltas={volume((t) => t.addToCarts, formatInteger)} />
        <StatTile
          label="Taux d'engagement"
          value={totals?.engagementRatePercent != null ? formatPercent(totals.engagementRatePercent) : '—'}
          deltas={rate((t) => t.engagementRatePercent)}
          hint="sessions de plus de 10 s, de 2 pages ou avec conversion"
        />
        <StatTile
          label="Durée moyenne de la session"
          value={formatDuration(totals?.avgSessionSeconds ?? null)}
          deltas={volume((t) => t.avgSessionSeconds, (value) => formatDuration(value))}
        />
      </div>
      <div className="kpi-row">
        <StatTile
          label="Tx Paniers"
          value={totals?.cartRatePercent != null ? formatPercent(totals.cartRatePercent) : '—'}
          deltas={rate((t) => t.cartRatePercent)}
          hint="ajouts au panier ÷ sessions"
        />
        <StatTile label="Achats d'e-commerce" value={formatInteger(totals?.purchases ?? 0)} deltas={volume((t) => t.purchases, formatInteger)} hint="achats vus par GA4" />
        <StatTile
          label="CR%"
          value={totals?.conversionPercent != null ? formatPercent(totals.conversionPercent) : '—'}
          deltas={rate((t) => t.conversionPercent)}
          hint="achats GA4 ÷ sessions — le CR% sur les commandes Shopify est sur le Résumé"
        />
      </div>

      <h2 className="section-heading">Performances over time</h2>
      <div className="chart-grid">
        <Ga4MetricsTable
          title="Jour par jour"
          subtitle="Le plus récent en haut — total : celui de GA4 pour la période"
          labelHeader="Date"
          rows={dailyDesc}
          label={(row) => formatDayShort(row.date)}
          rowKey={(row) => row.date}
          total={totals}
        />
        <SalesTrendChart
          className="chart-grid__wide"
          title="Nombre total d'utilisateurs"
          subtitle="Utilisateurs par jour (une tranche de plusieurs jours additionne ses jours)"
          current={stats?.daily ?? []}
          reference={[]}
          referenceLabel=""
          currentLabel="Utilisateurs"
          value={(point) => point.users}
          formatValue={formatInteger}
          formatAxis={formatCompact}
          dots
        />
      </div>

      <h2 className="section-heading">Performances by channel</h2>
      <div className="chart-grid">
        <Ga4MetricsTable
          title="Par canal"
          subtitle="Groupe de canaux par défaut de la session, le plus d'utilisateurs en haut"
          labelHeader="Canal"
          rows={stats?.byChannel ?? []}
          label={(row) => row.value}
          rowKey={(row) => row.value}
          total={totals}
        />
        <Ga4SeriesChart title="Utilisateurs par canal" noun="canaux" dates={dates} daily={stats?.channelDaily ?? []} labelOf={channelLabel} />
        <DonutChart
          title="Sessions par canal"
          subtitle="Part des sessions de la période"
          slices={shareSlices(stats?.byChannel ?? [], (row) => row.sessions, channelLabel)}
          formatValue={formatInteger}
          emptyMessage="Aucune session sur cette période."
        />
        <DonutChart
          title="Achats par canal"
          subtitle="Part des achats GA4 de la période"
          slices={shareSlices(stats?.byChannel ?? [], (row) => row.purchases, channelLabel)}
          formatValue={formatInteger}
          emptyMessage="Aucun achat sur cette période."
        />
        <Ga4ConversionBars title="CR% par canal" rows={conversionRows(stats?.byChannel ?? [], channelLabel)} minSessions={MIN_SESSIONS_CHANNEL} />
      </div>

      <h2 className="section-heading">Performances by country</h2>
      <div className="chart-grid">
        <Ga4MetricsTable
          title="Par pays"
          subtitle="Pays des visiteurs, le plus d'utilisateurs en haut"
          labelHeader="Pays"
          rows={stats?.byCountry ?? []}
          label={(row) => countryName(row.value)}
          rowKey={(row) => row.value}
          total={totals}
        />
        <Ga4SeriesChart title="Utilisateurs par pays" noun="pays" dates={dates} daily={stats?.countryDaily ?? []} labelOf={countryName} />
        <DonutChart
          title="Sessions par pays"
          subtitle="Part des sessions de la période"
          slices={shareSlices(stats?.byCountry ?? [], (row) => row.sessions, countryName)}
          formatValue={formatInteger}
          emptyMessage="Aucune session sur cette période."
        />
        <DonutChart
          title="Achats par pays"
          subtitle="Part des achats GA4 de la période"
          slices={shareSlices(stats?.byCountry ?? [], (row) => row.purchases, countryName)}
          formatValue={formatInteger}
          emptyMessage="Aucun achat sur cette période."
        />
        <Ga4ConversionBars title="CR% par pays" rows={conversionRows(stats?.byCountry ?? [], countryName)} minSessions={MIN_SESSIONS_COUNTRY} />
      </div>
    </SectionShell>
  );
}
